import type { ThemeDefinition } from 'vuetify';

import { ThemeName } from '@/types/theme';
import { colorPalette } from './color-palette';
import { generateVuetifyColors } from './generate-color-palette';
import { colors } from './legacy-palette';
import { darkOpacity, lightOpacity, sharedOpacity } from './opacity';

interface OnColor {
  contrastLight: string;
  contrastDark: string;
}

// Generate the colors for the new colour palette
const paletteLight: ThemeDefinition['colors'] =
  generateVuetifyColors(colorPalette, ThemeName.light) || {};

const paletteDark: ThemeDefinition['colors'] =
  generateVuetifyColors(colorPalette, ThemeName.dark) || {};

const sharedVariables: ThemeDefinition['variables'] = {
  ...sharedOpacity,
  'text-secondary': 'var(--v-medium-emphasis-opacity)',
  'hover-box-shadow': '0 4px 12px rgba(var(--v-theme-secondary-darker), 0.08)',
  'scrollbar-width': '6px'
};

const onLight: OnColor = {
  contrastLight: paletteLight?.['gray-1000'] || '#22303e',
  contrastDark: paletteLight?.['gray-10'] || '#fbfbfd'
};

const onDark: OnColor = {
  contrastLight: paletteDark?.['gray-800'] || '#e6e6f1',
  contrastDark: paletteDark?.['gray-10'] || '#232333'
};

const themeLight: ThemeDefinition = {
  dark: false,
  colors: {
    ...colors,
    ...paletteLight,
    background: paletteLight?.['gray-25'] || '#F6F6F8',
    'on-background': onLight.contrastLight,
    surface: paletteLight?.['gray-0'] || '#ffffff',
    'on-surface': onLight.contrastLight,
    white: paletteLight?.['gray-0'] || '#ffffff',
    'on-white': onLight.contrastLight,
    black: paletteLight?.['gray-1200'] || '#121a21',
    'on-black': onLight.contrastDark,
    chat: paletteLight?.['gray-50'] || '#f3f4fd',
    'on-chat': onLight.contrastLight,
    // Gradient end stop with no palette token in Figma; the nearest shade,
    // info-soft (#58D0D2), is visibly off.
    'ai-accent': '#3BC6D7',
    'surface-variant': onLight.contrastLight,
    'on-surface-variant': onLight.contrastDark,
    // 'brand-text': colors.bm_blue, // Old school blue, white on dark mode
    brand: colors.bm_blue, // Old school blue, white on dark mode
    'on-brand': paletteLight?.['gray-0'] || '#ffffff', // white text, blue on dark mode
    track: paletteLight?.['gray-75'] || '#EDEDF0',
    'on-track': onLight.contrastLight,
    'perfect-scrollbar-track': paletteLight?.['secondary-400'] || '#b0b8c4',
    'perfect-scrollbar-thumb': paletteLight?.['gray-200'] || '#d0d4da',
    avatar: paletteLight?.['gray-75'] || '#ecedf8',
    'on-avatar': onLight.contrastLight,
    'map-ink': paletteLight?.['gray-1000'] || '#22303E',
    'on-map-ink': onLight.contrastDark,
    'map-ink-contrast': paletteLight?.['gray-10'] || '#FBFBFD',
    'on-map-ink-contrast': onLight.contrastLight
  },
  variables: {
    ...sharedVariables,
    ...lightOpacity,
    'border-color': paletteLight?.['gray-1000'] || '#22303e',
    'text-disabled': 'var(--v-disabled-opacity)',
    // Shadows
    'shadow-key-umbra-color': '34, 48, 62',
    'shadow-xs-opacity': 0.06,
    'shadow-sm-opacity': 0.08,
    'shadow-md-opacity': 0.1,
    'shadow-lg-opacity': 0.14,
    'shadow-xl-opacity': 0.18
  }
};

const themeDark: ThemeDefinition = {
  dark: true,
  colors: {
    ...colors,
    ...paletteDark,
    background: paletteDark?.['gray-10'] || '#232333',
    'on-background': onDark.contrastLight,
    surface: paletteDark?.['gray-25'] || '#2a2b3d',
    'on-surface': onDark.contrastLight,
    white: paletteDark?.['gray-1200'] || '#121a21',
    'on-white': paletteDark?.['gray-10'] || '#232333',
    black: paletteDark?.['gray-0'] || '#ffffff',
    'on-black': paletteDark?.['gray-800'] || '#e6e6f1',
    chat: paletteDark?.['gray-10'] || '#232333',
    'on-chat': onDark.contrastLight,
    // Theme-independent, unlike the `primary` stop it pairs with — see the
    // light theme note.
    'ai-accent': '#3BC6D7',
    'surface-variant': paletteDark?.['gray-800'] || '#e6e6f1',
    'on-surface-variant': paletteDark?.['gray-25'] || '#2a2b3d',
    // 'brand-text': paletteDark?.['gray-1200'] || '#ffffff', // Old school blue, white on dark mode
    brand: paletteDark?.['gray-1200'] || '#ffffff', // Old school blue, white on dark mode
    'on-brand': colors.bm_blue, // white text, blue on dark mode
    track: '#41415F',
    'on-track': onDark.contrastLight,
    'perfect-scrollbar-track': paletteDark?.['secondary-400'] || '#b0b8c4',
    'perfect-scrollbar-thumb': paletteDark?.['gray-400'] || '#9fa6b2',
    avatar: paletteDark?.['gray-75'] || '#ecedf8',
    'on-avatar': onDark.contrastLight,
    'map-ink': paletteDark?.['gray-1000'] || '#FAFAFD',
    'on-map-ink': onDark.contrastDark,
    'map-ink-contrast': paletteDark?.['gray-10'] || '#232333',
    'on-map-ink-contrast': onDark.contrastLight
  },
  variables: {
    ...sharedVariables,
    ...darkOpacity,
    'border-color': paletteDark?.['gray-800'] || '#e6e6f1',
    'text-disabled': 'var(--v-disabled-opacity)',
    // Shadows
    'shadow-key-umbra-color': '20, 20, 29',
    'shadow-xs-opacity': 0.18,
    'shadow-sm-opacity': 0.2,
    'shadow-md-opacity': 0.22,
    'shadow-lg-opacity': 0.24,
    'shadow-xl-opacity': 0.26
  }
};

export const theme = {
  defaultTheme: 'light',
  themes: {
    light: themeLight,
    dark: themeDark
  }
};
